/**
 * Validates KyberSwap calldata before a transaction is signed/submitted.
 * Prevents loss of funds from malformed routes (e.g. plain transfers instead of swaps).
 */

// 4-byte function selectors that indicate a plain token movement, NOT a swap.
// If the calldata starts with one of these, the route is broken.
const DANGEROUS_SELECTORS: Record<string, string> = {
  '0xa9059cbb': 'ERC20.transfer(address,uint256)',
  '0x23b872dd': 'ERC20.transferFrom(address,address,uint256)',
  '0x095ea7b3': 'ERC20.approve(address,uint256)',
  '0x2e1a7d4d': 'WETH.withdraw(uint256)',
  '0xd0e30db0': 'WETH.deposit()',
}

// Minimum calldata length for a meaningful swap. A 4-byte selector + at least
// one 32-byte word = 72 hex chars after "0x" (4+32=36 bytes -> 72 hex).
const MIN_SWAP_DATA_LENGTH = 74 // "0x" + 72

export interface KyberSwapTxParams {
  to: string
  data: string
  value: string
  tokenIn: string
  tokenOut: string
  sender: string
  chainId: number
  quotedAmountOut: string
  buildAmountOut: string
}

export interface ValidationResult {
  valid: boolean
  reason?: string
  code?: string
}

function normalizeAddress(addr: string): string {
  return (addr || '').toLowerCase().trim()
}

/**
 * Check 1: Calldata must exist and have minimum length for a swap.
 * A plain ETH transfer has data = "0x" or empty.
 */
export function validateCalldataExists(data: string): ValidationResult {
  if (!data || data === '0x' || data === '0X' || data.length < MIN_SWAP_DATA_LENGTH) {
    return {
      valid: false,
      reason: `Calldata too short or empty (${data?.length ?? 0} chars). This looks like a plain transfer, not a swap.`,
      code: 'EMPTY_OR_SHORT_CALLDATA',
    }
  }
  return { valid: true }
}

/**
 * Check 2: Calldata must NOT start with a known dangerous function selector
 * (transfer, transferFrom, approve, WETH withdraw/deposit).
 */
export function validateCalldataSelector(data: string): ValidationResult {
  if (!data || data.length < 10) {
    return { valid: false, reason: 'Calldata missing function selector', code: 'NO_SELECTOR' }
  }
  const selector = data.slice(0, 10).toLowerCase()
  const match = DANGEROUS_SELECTORS[selector]
  if (match) {
    return {
      valid: false,
      reason: `Calldata selector ${selector} matches ${match}. This is NOT a swap.`,
      code: 'DANGEROUS_SELECTOR',
    }
  }
  return { valid: true }
}

/**
 * Check 3: The target (to) address must NOT be the sender's own address.
 * Sending to yourself with swap calldata = guaranteed loss.
 */
export function validateTargetNotSender(to: string, sender: string): ValidationResult {
  if (normalizeAddress(to) === normalizeAddress(sender)) {
    return {
      valid: false,
      reason: `Target address ${to} is the sender's own address. Swap would be a self-transfer.`,
      code: 'TARGET_IS_SENDER',
    }
  }
  return { valid: true }
}

/**
 * Check 4: The target must NOT be one of the traded token addresses.
 * If to == tokenIn, this would call the token contract (likely a transfer).
 * If to == tokenOut, same problem.
 */
export function validateTargetNotToken(to: string, tokenIn: string, tokenOut: string): ValidationResult {
  const normalizedTo = normalizeAddress(to)
  if (normalizedTo === normalizeAddress(tokenIn)) {
    return {
      valid: false,
      reason: `Target address ${to} is the input token contract. This is not a router.`,
      code: 'TARGET_IS_TOKEN_IN',
    }
  }
  if (normalizedTo === normalizeAddress(tokenOut)) {
    return {
      valid: false,
      reason: `Target address ${to} is the output token contract. This is not a router.`,
      code: 'TARGET_IS_TOKEN_OUT',
    }
  }
  return { valid: true }
}

/**
 * Check 5: The target must be a valid Ethereum address.
 */
export function validateTargetIsAddress(to: string): ValidationResult {
  if (!to || !/^0x[0-9a-fA-F]{40}$/.test(to)) {
    return {
      valid: false,
      reason: `Target address "${to}" is not a valid Ethereum address.`,
      code: 'INVALID_TARGET_ADDRESS',
    }
  }
  const ZERO_ADDR = '0x0000000000000000000000000000000000000000'
  if (normalizeAddress(to) === ZERO_ADDR) {
    return {
      valid: false,
      reason: 'Target address is the zero address.',
      code: 'TARGET_IS_ZERO',
    }
  }
  return { valid: true }
}

/**
 * Check 6: The build's amountOut should not deviate wildly from the quoted amountOut.
 * A >50% deviation indicates something is very wrong with the route.
 */
export function validateAmountOutDeviation(
  quotedAmountOut: string,
  buildAmountOut: string,
  maxDeviationPercent = 50
): ValidationResult {
  const quoted = BigInt(quotedAmountOut || '0')
  const built = BigInt(buildAmountOut || '0')

  if (quoted === 0n) {
    return { valid: false, reason: 'Quoted amount out is zero.', code: 'ZERO_QUOTED_AMOUNT' }
  }
  if (built === 0n) {
    return { valid: false, reason: 'Build amount out is zero. No tokens would be received.', code: 'ZERO_BUILD_AMOUNT' }
  }

  const deviationBps = Number(((quoted - built) * 10000n) / quoted)
  if (deviationBps > maxDeviationPercent * 100) {
    return {
      valid: false,
      reason: `Build amountOut deviates ${(deviationBps / 100).toFixed(1)}% from quote (quoted: ${quotedAmountOut}, build: ${buildAmountOut}). Max allowed: ${maxDeviationPercent}%.`,
      code: 'EXCESSIVE_AMOUNT_DEVIATION',
    }
  }

  return { valid: true }
}

/**
 * Check 7: For native ETH input, value must be non-zero.
 * For token input, value should be zero (tokens are pulled via transferFrom).
 */
export function validateValueMatchesNative(
  value: string,
  tokenIn: string,
  isNativeInput: boolean
): ValidationResult {
  const numValue = BigInt(value || '0')

  if (isNativeInput && numValue === 0n) {
    return {
      valid: false,
      reason: 'Native token input but transaction value is zero. ETH would not be sent.',
      code: 'NATIVE_INPUT_ZERO_VALUE',
    }
  }

  if (!isNativeInput && numValue > 0n) {
    return {
      valid: false,
      reason: `Token input but transaction includes ETH value (${value}). This ETH may be lost.`,
      code: 'TOKEN_INPUT_HAS_VALUE',
    }
  }

  return { valid: true }
}

/**
 * Run ALL validation checks. Returns the first failure or { valid: true }.
 */
export function validateKyberSwapTransaction(params: KyberSwapTxParams): ValidationResult {
  const NATIVE_ADDRESSES = [
    '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
    '0x0000000000000000000000000000000000000000',
    '',
    'eth', 'matic', 'bnb', 'avax',
  ]
  const isNativeInput = NATIVE_ADDRESSES.includes(normalizeAddress(params.tokenIn))

  const checks: ValidationResult[] = [
    validateTargetIsAddress(params.to),
    validateCalldataExists(params.data),
    validateCalldataSelector(params.data),
    validateTargetNotSender(params.to, params.sender),
    validateTargetNotToken(params.to, params.tokenIn, params.tokenOut),
    validateAmountOutDeviation(params.quotedAmountOut, params.buildAmountOut),
    validateValueMatchesNative(params.value, params.tokenIn, isNativeInput),
  ]

  for (const result of checks) {
    if (!result.valid) return result
  }

  return { valid: true }
}
